import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useState } from "react";
import { useGetAllAccountsQuery } from "store/queries/account";
import { useGetAllPayersQuery } from "store/queries/payers";
import {
  IPayerAutoSelectionRule,
  IPayerAutoSelectionRuleSaveRequest,
  PayerAutoSelectionPayerType,
  PayerAutoSelectionRuleKind,
  PayerAutoSelectionSourceScope,
} from "types/PayerAutoSelection/PayerAutoSelection";

import {
  KEYWORD_CAPTION,
  KIND_CHOICES,
  MEDICARE_ADVANTAGE_DEFAULTS,
  KIND_EXPLANATIONS,
  MEDICARE_ADVANTAGE_TARGET,
  MIN_KEYWORD_LENGTH,
  OPTION_TEXT,
  PAYER_TYPE_CHOICES,
  SHORT_KEYWORD_PROBLEM,
  SOURCE_CHOICES,
  SourceMode,
  sourceModeOf,
} from "./payerAutoSelectionWording";

interface IDraft {
  kind: PayerAutoSelectionRuleKind;
  /** Keywords, one chip each, as typed. */
  keywords: string[];
  /** Exclude keywords, one chip each, as typed. */
  excludeKeywords: string[];
  matchPolicyTypeMedicaid: boolean;
  targetPayerId: number | null;
  accountIds: string[];
  sourceScope: PayerAutoSelectionSourceScope;
  /** The dialog's single "Switch from" choice; see `sourceModeOf`. */
  sourceMode: SourceMode;
  sourcePayerIds: number[];
  sourcePayerTypes: PayerAutoSelectionPayerType[];
  wholeSegment: boolean;
  skipOutOfState: boolean;
  enabled: boolean;
}

const toDraft = (rule: IPayerAutoSelectionRule | null): IDraft => ({
  kind: rule?.kind ?? "PRODUCT_KEYWORD",
  keywords: rule?.keywords ?? [],
  excludeKeywords: rule?.excludeKeywords ?? [],
  matchPolicyTypeMedicaid: rule?.matchPolicyTypeMedicaid ?? false,
  targetPayerId: rule?.targetPayerId ?? null,
  accountIds: rule?.accountIds ?? [],
  sourceScope: rule?.sourceScope ?? "SAME_PVERIFY_CODE",
  sourceMode: sourceModeOf(
    rule?.sourceScope ?? "SAME_PVERIFY_CODE",
    rule?.sourcePayerTypes ?? []
  ),
  sourcePayerIds: rule?.sourcePayerIds ?? [],
  sourcePayerTypes: rule?.sourcePayerTypes ?? [],
  wholeSegment: rule?.wholeSegment ?? false,
  skipOutOfState: rule?.skipOutOfState ?? false,
  enabled: rule?.enabled ?? true,
});

/** One keyword per line; commas separate too, because a keyword never contains one. */
export const parseKeywords = (text: string): string[] => [
  ...new Set(
    text
      .split(/[\n,]/)
      .map((keyword) =>
        keyword.toUpperCase().replace(/[-_]/g, " ").replace(/\s+/g, " ").trim()
      )
      .filter(Boolean)
  ),
];

/** What is wrong with the draft, in words, or null. The API checks the same things. */
export const draftProblem = (draft: IDraft): string | null => {
  const keywords = parseKeywords(draft.keywords.join("\n"));
  const isMedicareAdvantage = draft.kind === "MEDICARE_ADVANTAGE";
  if (!isMedicareAdvantage && draft.targetPayerId === null) {
    return "Choose the payer to switch to.";
  }
  if (
    (draft.kind === "PRODUCT_KEYWORD" || isMedicareAdvantage) &&
    keywords.length === 0
  ) {
    return "Enter at least one keyword.";
  }
  if (
    [...keywords, ...parseKeywords(draft.excludeKeywords.join("\n"))].some(
      (keyword) => keyword.length < MIN_KEYWORD_LENGTH
    )
  ) {
    return SHORT_KEYWORD_PROBLEM;
  }
  if (
    draft.kind === "MEDICAID_DUAL" &&
    keywords.length === 0 &&
    !draft.matchPolicyTypeMedicaid
  ) {
    return "Enter at least one keyword, or match PolicyType = Medicaid.";
  }
  if (
    draft.sourceMode === "LISTED_PAYERS" &&
    draft.sourcePayerIds.length === 0
  ) {
    return "Choose at least one payer to switch from.";
  }
  if (
    draft.sourceMode === "PAYER_TYPES" &&
    draft.sourcePayerTypes.length === 0
  ) {
    return "Choose at least one payer type to switch from.";
  }
  return null;
};

/**
 * The body the API receives: fields that do not apply to the kind are sent cleared. A Medicare
 * Advantage rule has no target and no keyword options. Exclude keywords and payer types apply to
 * every kind.
 */
export const toSaveRequest = (
  draft: IDraft,
  priority: number
): IPayerAutoSelectionRuleSaveRequest => {
  const isKeyword = draft.kind === "PRODUCT_KEYWORD";
  const takesKeywords = draft.kind !== "OUT_OF_AREA_DEFAULT";
  return {
    kind: draft.kind,
    keywords: takesKeywords ? parseKeywords(draft.keywords.join("\n")) : [],
    excludeKeywords: parseKeywords(draft.excludeKeywords.join("\n")),
    matchPolicyTypeMedicaid:
      draft.kind === "MEDICAID_DUAL" && draft.matchPolicyTypeMedicaid,
    targetPayerId:
      draft.kind === "MEDICARE_ADVANTAGE" ? null : draft.targetPayerId,
    accountIds: draft.accountIds,
    sourceScope:
      draft.sourceMode === "PAYER_TYPES" ? "ANY_PAYER" : draft.sourceMode,
    sourcePayerIds:
      draft.sourceMode === "LISTED_PAYERS" ? draft.sourcePayerIds : [],
    sourcePayerTypes:
      draft.sourceMode === "PAYER_TYPES" ? draft.sourcePayerTypes : [],
    wholeSegment: isKeyword && draft.wholeSegment,
    skipOutOfState: isKeyword && draft.skipOutOfState,
    enabled: draft.enabled,
    priority,
  };
};

interface IPayerAutoSelectionRuleDialogProps {
  visible: boolean;
  /** The rule being edited, or null when adding. */
  rule: IPayerAutoSelectionRule | null;
  saving: boolean;
  /** The priority a NEW rule gets: one past the last rule, so it is appended at the end of the order. */
  nextPriority: number;
  onSave: (body: IPayerAutoSelectionRuleSaveRequest) => void;
  onHide: () => void;
}

const listOf = (data: any): any[] => (data as any)?.data || data || [];

export function PayerAutoSelectionRuleDialog({
  visible,
  rule,
  saving,
  nextPriority,
  onSave,
  onHide,
}: IPayerAutoSelectionRuleDialogProps) {
  const [draft, setDraft] = useState<IDraft>(toDraft(rule));
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setDraft(toDraft(rule));
      setProblem(null);
    }
  }, [visible, rule]);

  const { data: payersData } = useGetAllPayersQuery(undefined, {
    skip: !visible,
  });
  const { data: accountsData } = useGetAllAccountsQuery(null, {
    skip: !visible,
  });

  const payerOptions = sortBy(
    listOf(payersData)
      .filter((payer: any) => !payer.archivedAt)
      .map((payer: any) => ({ label: payer.name, value: payer.id })),
    ["label"]
  );
  const accountOptions = sortBy(
    listOf(accountsData).map((account: any) => ({
      label: account.name,
      value: account.id,
    })),
    ["label"]
  );

  const set = (patch: Partial<IDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const isMedicareAdvantage = draft.kind === "MEDICARE_ADVANTAGE";
  const takesKeywords = draft.kind !== "OUT_OF_AREA_DEFAULT";

  /**
   * Choosing Medicare Advantage for a NEW rule fills in the seeded rule's words and payer type, but
   * only where the admin has not typed anything yet.
   */
  const chooseKind = (kind: PayerAutoSelectionRuleKind) =>
    setDraft((current) => {
      if (kind !== "MEDICARE_ADVANTAGE" || rule) return { ...current, kind };
      return {
        ...current,
        kind,
        keywords:
          current.keywords.length > 0
            ? current.keywords
            : MEDICARE_ADVANTAGE_DEFAULTS.keywords,
        excludeKeywords:
          current.excludeKeywords.length > 0
            ? current.excludeKeywords
            : MEDICARE_ADVANTAGE_DEFAULTS.excludeKeywords,
        // Only while the admin has left "switch from" at its default: the seeded row switches from any carrier,
        // Commercial selections only.
        ...(current.sourceMode === "SAME_PVERIFY_CODE"
          ? {
              sourceMode: "PAYER_TYPES" as const,
              sourceScope: MEDICARE_ADVANTAGE_DEFAULTS.sourceScope,
              sourcePayerTypes: MEDICARE_ADVANTAGE_DEFAULTS.sourcePayerTypes,
            }
          : {}),
      };
    });

  const save = () => {
    const found = draftProblem(draft);
    setProblem(found);
    if (found) return;
    // An edit keeps the rule's place in the order, whatever its kind becomes.
    onSave(toSaveRequest(draft, rule ? rule.priority : nextPriority));
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        type="button"
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        data-testid="payerAutoSelection.dialog.cancel"
      />
      <Button
        type="button"
        label={rule ? "Save rule" : "Add rule"}
        loading={saving}
        onClick={save}
        data-testid="payerAutoSelection.dialog.save"
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={rule ? "Edit rule" : "Add a rule"}
      footer={footer}
      style={{ width: "min(680px, 95vw)" }}
      data-testid="payerAutoSelection.dialog"
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-1">
          <Label htmlFor="payerAutoSelection.kind">What kind of rule</Label>
          <Dropdown
            inputId="payerAutoSelection.kind"
            options={KIND_CHOICES}
            value={draft.kind}
            onChange={(event) => chooseKind(event.value)}
          />
        </div>

        <p
          className="m-0 text-color-secondary"
          data-testid="payerAutoSelection.kindExplanation"
        >
          {KIND_EXPLANATIONS[draft.kind]}
        </p>

        {takesKeywords && (
          <div className="flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.keywords">
              Plan name contains
            </Label>
            <Chips
              inputId="payerAutoSelection.keywords"
              data-testid="payerAutoSelection.keywords"
              value={draft.keywords}
              onChange={(event) => set({ keywords: event.value ?? [] })}
              separator=","
              addOnBlur
              allowDuplicate={false}
            />
            <small className="text-color-secondary">{KEYWORD_CAPTION}</small>
          </div>
        )}

        {draft.kind === "MEDICAID_DUAL" && (
          <div className="flex align-items-start gap-2">
            <Checkbox
              inputId="payerAutoSelection.matchPolicyTypeMedicaid"
              checked={draft.matchPolicyTypeMedicaid}
              onChange={(event) =>
                set({ matchPolicyTypeMedicaid: !!event.checked })
              }
            />
            <label htmlFor="payerAutoSelection.matchPolicyTypeMedicaid">
              {OPTION_TEXT.matchPolicyTypeMedicaid.label}
              <small className="block text-color-secondary">
                {OPTION_TEXT.matchPolicyTypeMedicaid.help}
              </small>
            </label>
          </div>
        )}

        <div className="flex flex-column gap-1">
          <Label htmlFor="payerAutoSelection.excludeKeywords">
            Never when the plan name contains (optional)
          </Label>
          <Chips
            inputId="payerAutoSelection.excludeKeywords"
            data-testid="payerAutoSelection.excludeKeywords"
            value={draft.excludeKeywords}
            onChange={(event) => set({ excludeKeywords: event.value ?? [] })}
            separator=","
            addOnBlur
            allowDuplicate={false}
          />
          <small className="text-color-secondary">{KEYWORD_CAPTION}</small>
        </div>

        {isMedicareAdvantage && (
          <div className="flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.maTarget">Switch to</Label>
            <span
              id="payerAutoSelection.maTarget"
              data-testid="payerAutoSelection.maTarget"
            >
              {MEDICARE_ADVANTAGE_TARGET}
            </span>
          </div>
        )}

        {!isMedicareAdvantage && (
          <div className="flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.target">Switch to</Label>
            <Dropdown
              inputId="payerAutoSelection.target"
              options={payerOptions}
              value={draft.targetPayerId}
              onChange={(event) => set({ targetPayerId: event.value ?? null })}
              placeholder="Choose a payer"
              filter
            />
            <small className="text-color-secondary">
              Only fires at a facility where this payer is set up.
            </small>
          </div>
        )}

        <div className="flex flex-column gap-1">
          <Label htmlFor="payerAutoSelection.accounts">Facilities</Label>
          <MultiSelect
            inputId="payerAutoSelection.accounts"
            options={accountOptions}
            value={draft.accountIds}
            onChange={(event) => set({ accountIds: event.value ?? [] })}
            placeholder="All facilities"
            display="chip"
            filter
          />
          <small className="text-color-secondary">
            None selected means all facilities.
          </small>
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="payerAutoSelection.source">Switch from</Label>
          <Dropdown
            inputId="payerAutoSelection.source"
            options={SOURCE_CHOICES}
            value={draft.sourceMode}
            onChange={(event) => set({ sourceMode: event.value })}
          />
        </div>

        {draft.sourceMode === "PAYER_TYPES" && (
          <div className="flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.sourcePayerTypes">
              Payer types this rule switches from
            </Label>
            <MultiSelect
              inputId="payerAutoSelection.sourcePayerTypes"
              options={PAYER_TYPE_CHOICES}
              value={draft.sourcePayerTypes}
              onChange={(event) => set({ sourcePayerTypes: event.value ?? [] })}
              display="chip"
            />
          </div>
        )}

        {draft.sourceMode === "LISTED_PAYERS" && (
          <div className="flex flex-column gap-1">
            <Label htmlFor="payerAutoSelection.sourcePayers">
              Payers this rule switches from
            </Label>
            <MultiSelect
              inputId="payerAutoSelection.sourcePayers"
              options={payerOptions}
              value={draft.sourcePayerIds}
              onChange={(event) => set({ sourcePayerIds: event.value ?? [] })}
              display="chip"
              filter
            />
          </div>
        )}

        {draft.kind === "PRODUCT_KEYWORD" &&
          (["wholeSegment", "skipOutOfState"] as const).map((option) => (
            <div key={option} className="flex align-items-start gap-2">
              <Checkbox
                inputId={`payerAutoSelection.${option}`}
                checked={draft[option]}
                onChange={(event) => set({ [option]: !!event.checked })}
              />
              <label htmlFor={`payerAutoSelection.${option}`}>
                {OPTION_TEXT[option].label}
                <small className="block text-color-secondary">
                  {OPTION_TEXT[option].help}
                </small>
              </label>
            </div>
          ))}

        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="payerAutoSelection.enabled"
            checked={draft.enabled}
            onChange={(event) => set({ enabled: !!event.checked })}
          />
          <label htmlFor="payerAutoSelection.enabled">Enabled</label>
        </div>

        {problem && (
          <Message
            severity="error"
            text={problem}
            data-testid="payerAutoSelection.dialog.problem"
          />
        )}
      </div>
    </Dialog>
  );
}
